import { useState, useEffect, useCallback, useRef } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { withAbortSignal } from '../utils/abortSignal';

function isRequestCancelled(error) {
  return (
    error?.name === 'AbortError'
    || error?.name === 'CanceledError'
    || error?.code === 'ERR_CANCELED'
  );
}

export const useNotifications = (pollingInterval = 30000) => {
  const { user, loading: authLoading } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const isMountedRef = useRef(true);

  const fetchUnreadCount = useCallback(async (signal) => {
    try {
      const response = await api.get(
        '/notifications/unread-count',
        withAbortSignal({}, signal),
      );
      if (isMountedRef.current) {
        setUnreadCount(response.data?.count ?? 0);
      }
    } catch (error) {
      if (isRequestCancelled(error)) {
        return;
      }
      if (error.response?.status === 401) {
        if (isMountedRef.current) {
          setUnreadCount(0);
        }
        return;
      }
      console.error('Error fetching unread notifications count:', error);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    if (authLoading || !user) {
      setUnreadCount(0);
      return undefined;
    }

    const controller = new AbortController();
    fetchUnreadCount(controller.signal);

    const interval = setInterval(
      () => fetchUnreadCount(controller.signal),
      pollingInterval,
    );

    return () => {
      isMountedRef.current = false;
      controller.abort();
      clearInterval(interval);
    };
  }, [authLoading, user, fetchUnreadCount, pollingInterval]);

  const updateCount = useCallback((newCount) => {
    setUnreadCount(newCount);
  }, []);

  const refreshCount = useCallback(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  return {
    unreadCount,
    updateCount,
    refreshCount,
  };
};
